/**
 * Consent-gated GTM loader — single source of truth for the container.
 *
 * The GTM <script> snippet has been removed from public/index.html; this
 * module is now the ONLY place that knows the container ID, and GTM only
 * loads when either of these is true:
 *   1. The visitor has already granted analytics consent
 *      (`_ac_consent === "accepted"`, checked via utils/attribution.hasConsent).
 *   2. The visitor grants consent in the current session via ConsentBanner
 *      or the /legal/cookie-policy manager, which then calls loadGtm()
 *      inside the same click handler.
 *
 * Guards:
 *   - Never loads on /admin/** routes (internal sessions were polluting GA4).
 *   - Idempotent — repeated calls do nothing after the first successful load.
 *   - dataLayer is initialised eagerly so pre-consent pushes from
 *     src/lib/gtm.js queue and are delivered by GTM once it initialises.
 *
 * Prerender safety: Puppeteer bots have no `_ac_consent` cookie, so
 * hasConsent() returns false and GTM is never injected into the HTML
 * emitted to prerender-cache. Bots receive content, not tags.
 */
import { hasConsent } from "../utils/attribution";

export const GTM_CONTAINER_ID = "GTM-K2M6W7K";

let _loaded = false;

// ─── dataLayer bootstrap (independent of GTM script) ──────────────────
// gtmPush() from src/lib/gtm.js pushes to window.dataLayer regardless of
// whether GTM has loaded. GTM's own installer creates the array too, but
// we do it up front so pre-consent pushes have a queue to land in. Once
// GTM's gtm.js runs, its internal init drains this queue and starts
// evaluating triggers.
export function ensureDataLayer() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
}

if (typeof window !== "undefined") {
  ensureDataLayer();
}

// ─── Admin-exclusion helper ───────────────────────────────────────────
export function isAdminRoute(pathname) {
  const p = pathname || (typeof window !== "undefined" ? window.location.pathname : "");
  return typeof p === "string" && p.startsWith("/admin");
}

// ─── Loader ───────────────────────────────────────────────────────────
/**
 * Inject the GTM <script> tag and the accessibility <noscript> fallback.
 * Idempotent + safe to call before consent (silent no-op).
 * Returns true if a load was performed on this call, false otherwise.
 */
export function loadGtm({ force = false } = {}) {
  if (typeof window === "undefined" || typeof document === "undefined") return false;
  if (_loaded) return false;
  if (isAdminRoute()) return false;
  if (!force && !hasConsent()) return false;

  ensureDataLayer();
  // GTM's canonical bootstrap event — mirrors what the removed inline
  // snippet used to do. Puts the tag-manager into "start" state so any
  // events already queued (fired before this loader ran) are eligible
  // for immediate trigger evaluation as soon as gtm.js parses.
  window.dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });

  // Injected <script> — matches the original snippet, just gated.
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;
  s.setAttribute("data-ac-gtm", "1");
  const first = document.getElementsByTagName("script")[0];
  if (first && first.parentNode) {
    first.parentNode.insertBefore(s, first);
  } else {
    document.head.appendChild(s);
  }

  // <noscript> iframe fallback. Injected once so users with JS enabled
  // still match the pre-consent behaviour of the original static tag.
  try {
    if (!document.querySelector('iframe[data-ac-gtm-ns="1"]')) {
      const ns = document.createElement("noscript");
      const iframe = document.createElement("iframe");
      iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_CONTAINER_ID}`;
      iframe.height = "0";
      iframe.width = "0";
      iframe.style.display = "none";
      iframe.style.visibility = "hidden";
      iframe.setAttribute("data-ac-gtm-ns", "1");
      ns.appendChild(iframe);
      document.body.insertBefore(ns, document.body.firstChild);
    }
  } catch {
    // <noscript> injection is best-effort; JS clients don't need it.
  }

  _loaded = true;
  return true;
}

/** For tests — reset the loaded flag. Not exported for prod use. */
export function __resetForTests() { _loaded = false; }
